import React, { useState, useRef, useEffect } from 'react';
import { faChevronDown, faBuilding } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

export default function OrganizationSelector(): React.ReactElement {
  const navigate = useNavigate();
  const { currentOrganization, organizations, switchOrganization } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const handleClick = (): void => {
    if (organizations.length > 1) {
      setDropdownOpen(!dropdownOpen);
    } else {
      navigate('/');
    }
  };

  if (!currentOrganization) {
    return (
      <div className="flex items-center space-x-3 px-4 py-3">
        <img className="w-12 h-12 object-contain" src="/logoipsum-custom-logogram.svg" alt="logo" />
        <div className="flex flex-col items-start">
          <h1 className="text-xl font-bold text-gray-900">OptoSoft</h1>
          <p className="text-xs text-gray-500">Carregando Organização...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-w-96 lg:min-w-fit" ref={dropdownRef}>
      <button
        onClick={handleClick}
        className={`flex items-center transition-all duration-200
          space-x-2 lg:w-full lg:space-x-3 lg:px-4 lg:py-3 lg:text-left
          ${
            organizations.length > 1
              ? 'hover:opacity-80 cursor-pointer lg:hover:opacity-100 lg:hover:bg-blue-50 lg:hover:shadow-sm'
              : 'hover:opacity-80 cursor-pointer'
          }`}
      >
        <img
          className="w-8 h-8 lg:w-12 lg:h-12 object-contain flex-shrink-0"
          src={
            currentOrganization?.logo_url
              ? currentOrganization.logo_url
              : '/logoipsum-custom-logogram.svg'
          }
          alt="logo"
        />
        <div className="flex flex-col items-start min-w-0 lg:flex-grow">
          <h1 className="text-lg font-semibold text-gray-900 truncate lg:text-xl lg:font-bold lg:w-full lg:text-nowrap">
            {currentOrganization.name}
          </h1>
          {currentOrganization.slogan && (
            <>
              {/* Mobile: Simple slogan text */}
              <p className="text-xs text-gray-500 truncate lg:hidden">
                {currentOrganization.slogan}
              </p>
              {/* Desktop: Slogan with building icon */}
              <div className="hidden lg:flex lg:items-center lg:space-x-1">
                <FontAwesomeIcon icon={faBuilding} className="w-3 h-3 text-gray-400" />
                <p className="text-xs text-gray-500 capitalize text-nowrap">
                  {currentOrganization.slogan}
                </p>
              </div>
            </>
          )}
        </div>
        {organizations.length > 1 && (
          <FontAwesomeIcon
            icon={faChevronDown}
            className={`w-3 h-3 text-gray-400 transition-transform duration-200 flex-shrink-0 ${
              dropdownOpen ? 'rotate-180' : ''
            }`}
          />
        )}
      </button>

      {/* Dropdown Menu */}
      {dropdownOpen && organizations.length > 1 && (
        <div className="absolute top-full left-4 right-4 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          <div className="py-2">
            {organizations
              .filter(org => org.id !== currentOrganization.id)
              .map(org => (
                <button
                  key={org.id}
                  onClick={() => {
                    switchOrganization(org);
                    setDropdownOpen(false);
                  }}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center space-x-3 transition-colors"
                >
                  <img
                    className="w-8 h-8 object-contain flex-shrink-0 opacity-60"
                    src={org?.logo_url ? org.logo_url : '/logoipsum-custom-logogram.svg'}
                    alt="logo"
                  />
                  <div className="flex flex-col flex-grow min-w-0">
                    <span className="font-medium text-gray-900 truncate">{org.name}</span>
                    <div className="flex items-center space-x-1">
                      <FontAwesomeIcon icon={faBuilding} className="w-2.5 h-2.5 text-gray-400" />
                      <span className="text-xs text-gray-500 capitalize">{org.slogan}</span>
                    </div>
                  </div>
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
